import { useEffect, useState, useSyncExternalStore } from 'react';

// Successful code is shared between visits; failed requests are never cached.
export function createDeferredCatalogue(loaders) {
  const components = new Map();
  const pending = new Map();
  const listeners = new Set();
  const subscribe = listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  const isReady = type => !Object.prototype.hasOwnProperty.call(loaders, type) || components.has(type);
  function useReady(type) {
    return useSyncExternalStore(subscribe, () => isReady(type), () => isReady(type));
  }
  function load(type) {
    if (components.has(type)) return Promise.resolve(components.get(type));
    if (!pending.has(type)) {
      const request = Promise.resolve().then(() => loaders[type]()).then(module => {
        components.set(type, module.default);
        pending.delete(type);
        listeners.forEach(listener => listener());
        return module.default;
      }, error => {
        pending.delete(type);
        throw error;
      });
      pending.set(type, request);
    }
    return pending.get(type);
  }
  function Step({ type, ...props }) {
    const ready = useReady(type);
    const [failure, setFailure] = useState(null);
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
      if (ready) return undefined;
      let active = true;
      load(type).catch(() => {
        if (active) setFailure(type);
      });
      return () => { active = false; };
    }, [type, ready, attempt]);
    const Component = components.get(type);
    if (Component) return <Component {...props} />;
    if (ready) return null;
    if (failure === type) {
      return <div role="alert">
        <p>This section could not be loaded. Please try again.</p>
        <button type="button" onClick={() => {
          setFailure(null);
          setAttempt(value => value + 1);
        }}>Retry</button>
      </div>;
    }
    return <div role="status" aria-live="polite">Loading section...</div>;
  }
  return { Step, useReady, isReady };
}

const catalogue = createDeferredCatalogue({
  medication: () => import('../medication/Medication'),
  medication_allergies: () => import('../MedicationAllergies/MedicationAllergies'),
  pharmacy: () => import('../Pharmacy/Pharmacy'),
});

export const useCatalogueRendererReady = catalogue.useReady;
export default catalogue.Step;
